import React from "react";
import Backdrop from "../../../components/Backdrop";
import { useBackgroundNav } from "../../../contexts/backgroundNav";
import { buildLink, ensurePool, getPoolCursor, getPoolImage, getPoolSize, removePoolImage, savePoolCursor } from "./api";
import { defaultData, Props } from "./types";
import "./Unsplash.sass";
import UnsplashCredit from "./UnsplashCredit";

const Unsplash: React.FC<Props> = ({
  data = defaultData,
  loader,
  setData,
}) => {
  // Migrate old pause setting
  React.useEffect(() => {
    if (data.timeout === Number.MAX_SAFE_INTEGER) {
      setData({
        ...data,
        paused: true,
        timeout: defaultData.timeout,
      });
    }
  }, []);

  const [, forceUpdate] = React.useReducer((x) => x + 1, 0);
  const [poolReady, setPoolReady] = React.useState(false);

  // Bootstrap pool on mount
  React.useEffect(() => {
    loader.push();
    ensurePool(data).finally(() => {
      setPoolReady(true);
      loader.pop();
    });

    const pollInterval = setInterval(() => forceUpdate(), 500);
    return () => clearInterval(pollInterval);
  }, []);

  const cursor = getPoolCursor();
  const item = getPoolImage(cursor);
  const poolSize = getPoolSize();
  const url = item ? buildLink(item.src) : null;

  // loadedUrl is only set AFTER the browser has decoded the image.
  // Comparing it against url in render gives an instantaneous answer
  // — no one-frame gap where the undecoded image sits at opacity 1.
  const [loadedUrl, setLoadedUrl] = React.useState<string | null>(null);
  const isImgReady = url !== null && loadedUrl === url;

  const changeCursor = React.useCallback((delta: number) => {
    const size = getPoolSize();
    if (size === 0) return;
    const next = (cursor + delta + size) % size;
    savePoolCursor(next);
    forceUpdate();
  }, [cursor]);

  // Auto-rotation timer
  React.useEffect(() => {
    if (data.paused || data.timeout <= 0 || poolSize === 0) return;
    const intervalMs = data.timeout * 1000;
    const timer = setInterval(() => changeCursor(1), intervalMs);
    return () => clearInterval(timer);
  }, [data.paused, data.timeout, poolSize, changeCursor]);

  // Track image decoding — remove broken images from the pool
  const LOAD_TIMEOUT_MS = 30_000; // give up after 30 s, move to next

  React.useEffect(() => {
    if (!url) {
      setLoadedUrl(null);
      return;
    }

    let done = false;
    const img = new window.Image();

    const onDone = () => { done = true; };

    img.onload = () => {
      onDone();
      setLoadedUrl(url);
    };
    img.onerror = () => {
      onDone();
      console.warn("[Unsplash] image load failed, removing from pool:", item?.src);
      removePoolImage(cursor);
      forceUpdate();
    };

    const timeout = setTimeout(() => {
      if (done) return;
      console.warn("[Unsplash] image load timed out after %ds, removing from pool:", LOAD_TIMEOUT_MS / 1000, item?.src);
      removePoolImage(cursor);
      forceUpdate();
    }, LOAD_TIMEOUT_MS);

    img.src = url;

    return () => {
      img.onload = null;
      img.onerror = null;
      clearTimeout(timeout);
    };
  }, [url]);

  const goNext = React.useCallback(() => {
    if (getPoolSize() === 0) return;
    changeCursor(1);
  }, [changeCursor]);

  const goPrev = React.useCallback(() => {
    if (getPoolSize() === 0) return;
    changeCursor(-1);
  }, [changeCursor]);

  const { setNav } = useBackgroundNav();

  React.useEffect(() => {
    setNav({ goNext, goPrev });
    return () => setNav({ goNext: null, goPrev: null });
  }, [goNext, goPrev, setNav]);

  const handlePause = () => {
    setData({
      ...data,
      paused: !data.paused,
    });
  };

  return (
    <div className="Unsplash fullscreen">
      <Backdrop
        className="image fullscreen"
        ready={isImgReady}
        style={{ backgroundImage: loadedUrl ? `url(${loadedUrl})` : undefined }}
      />

      {!isImgReady && (
        <div className="Unsplash__loading">
          <div className="Unsplash__spinner" />
          <span>Loading…</span>
        </div>
      )}

      {item && window && window.innerWidth >= 768 ? (
        <UnsplashCredit
          credit={item.credit}
          source={item.source}
          sourceLink={item.sourceLink}
          paused={data.paused ?? false}
          onPause={handlePause}
          onPrev={goPrev}
          onNext={goNext}
          hideInfo={true}
          hideControls={true}
        />
      ) : null}
    </div>
  );
};

export default Unsplash;
